import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  useLocation,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import "./assets/styles/App.css";
import "./assets/styles/common/table.css";
import Layout from "./components/layout/layout";
import LoginPage from "./pages/login";
import { useIsAuthenticated } from "@azure/msal-react";
import AppRoutes from "./routes";
import { PermissionProvider } from "./contexts/permission-context";
import { TenantProvider, useTenant } from "./contexts/tenant-context";
import { useUserService } from "./services/userService";
import { Box, CircularProgress } from "@mui/material";

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }
  return <>{children}</>;
};

const SessionLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserSession } = useUserService();
  const { userSession, setUserSession } = useTenant();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  useEffect(() => {
    // Skip if already loaded or on no-access route
    if (userSession || location.pathname === '/no-access') {
      setIsLoading(false);
      return;
    }

    // Skip if on auth-loading route (it handles its own loading)
    if (location.pathname === '/auth-loading') {
      setIsLoading(false);
      return;
    }

    // For any other route (including /dashboard from MSAL redirect), load session if not attempted
    if (hasAttemptedLoad) {
      setIsLoading(false);
      return;
    }

    const loadSession = async () => {
      setHasAttemptedLoad(true);
      try {
        const session = await getUserSession();
        setUserSession(session);

        // Check if user is disabled
        if (!session.user.isEnabled) {
          navigate("/no-access", { state: { reason: 'no-permissions' } });
          return;
        }

        // Handle no tenants (brand new user or no access)
        if (!session.isGlobalAdmin && session.tenants.length === 0) {
          navigate("/no-access", { state: { reason: 'no-permissions' } });
          return;
        }

      } catch (error) {
        console.error("Session loading error:", error);
        navigate("/no-access", { state: { reason: 'error' } });
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, location.pathname, hasAttemptedLoad]);

  if (isLoading && location.pathname !== '/auth-loading') {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          bgcolor: "#000",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

const ProtectedRoutes: React.FC = () => {
  const location = useLocation();
  
  // Skip permission loading for auth-loading and no-access routes
  const skipPermissions = location.pathname === '/auth-loading' || location.pathname === '/no-access';
  
  if (skipPermissions) {
    return (
      <Layout>
        <AppRoutes />
      </Layout>
    );
  }
  
  // For other routes, wrap with PermissionProvider
  return (
    <PermissionProvider>
      <Layout>
        <AppRoutes />
      </Layout>
    </PermissionProvider>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <TenantProvider>
                <SessionLoader>
                  <ProtectedRoutes />
                </SessionLoader>
              </TenantProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
